import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants/srp';
import TradeContext, {
  useGetAcquisitionNameForApiRequest
} from '~/context/common/TradeContext';
import FinanceContext, {
  formatFinanceInfo
} from '~/context/srp/FinanceContext';
import InventoryContext from '~/context/srp/InventoryContext';
import SearchContext from '~/context/srp/SearchContext';
import type { ConditionalTile, RequestedFeature } from '~/contracts/srp';
import useGetLowInventory from '~/hooks/srp/services/useGetLowInventory';
import useConditionalTiles from '~/hooks/srp/useConditionalTiles';
import useGenerateAnalyticsData from '~/hooks/srp/useGenerateAnalyticsData';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';
import useIsRequiredInventoryDataAvailable from '~/hooks/srp/useIsRequiredInventoryDataAvailable';
import type {
  InventoryRequest,
  InventoryResponse
} from '~/services/srp/contracts';
import fetchInventory from '~/services/srp/fetchInventory';
import { generateErrorLogger } from '~/utilities/loggers';

const getPageSize = (conditionalTiles: ConditionalTile[]) =>
  MAX_PAGE_SIZE_DEFAULT - conditionalTiles.filter(v => v.active).length;

function useShouldFetch(args: {
  initialValue: boolean;
  isLoaded: boolean;
  isRequiredInventoryDataAvailable: boolean;
  request: InventoryRequest;
}): boolean {
  const { initialValue, isLoaded, isRequiredInventoryDataAvailable, request } =
    args;

  const [shouldFetch, setShouldFetch] = useState(initialValue);

  // NOTE: The order of these effects is important
  useSubsequentEffect(() => {
    if (isLoaded) setShouldFetch(false);
  }, [isLoaded]);

  useSubsequentEffect(() => {
    if (isRequiredInventoryDataAvailable) setShouldFetch(true);
  }, [isRequiredInventoryDataAvailable, request]);

  return shouldFetch;
}

export default function useGetInventory(
  fetchedInventoryOnServer?: boolean
): void {
  const {
    inventory: { data, setData, setError, setLoading, isLoaded },
    setPreviousSearchRequestId,
    setSimilarVehicles
  } = useContext(InventoryContext);
  const { setFinanceInfo, setIsFinanceInitialized } =
    useContext(FinanceContext);
  const {
    tradeInfoFromApis: { setData: setTradeInfo }
  } = useContext(SearchContext);
  const { setTradeInApplied } = useContext(TradeContext);
  const conditionalTiles = useConditionalTiles();
  const { request, conditionalRequestedFeatures } = useGenerateInventoryRequest(
    getPageSize(conditionalTiles)
  );
  const generateAnalyticsData = useGenerateAnalyticsData();
  const [inventoryResponse, setInventoryResponse] =
    useState<InventoryResponse>();
  const getLowInventory = useGetLowInventory();
  const isRequiredInventoryDataAvailable =
    useIsRequiredInventoryDataAvailable();
  const shouldFetch = useShouldFetch({
    initialValue: !fetchedInventoryOnServer && isRequiredInventoryDataAvailable,
    isLoaded,
    isRequiredInventoryDataAvailable,
    request
  });

  // Need value when fetching inventory not when generating the request
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const preferredAcquisitionNameRef = useRef(preferredAcquisitionName);

  useEffect(() => {
    preferredAcquisitionNameRef.current = preferredAcquisitionName;
  }, [preferredAcquisitionName]);

  const conditionalRequestedFeaturesRef = useRef<RequestedFeature[]>([]);
  useEffect(() => {
    conditionalRequestedFeaturesRef.current = conditionalRequestedFeatures;
  }, [conditionalRequestedFeatures]);

  const previousSearchRequestIdRef = useRef(data.searchRequestId);
  useEffect(() => {
    previousSearchRequestIdRef.current = data.searchRequestId;
  }, [data.searchRequestId]);

  const requestForLowInventoryRef = useRef<InventoryRequest>();
  useEffect(() => {
    requestForLowInventoryRef.current = {
      ...request,
      analyticsData: generateAnalyticsData()
    };
  }, [generateAnalyticsData, request]);

  useEffectWithController(
    controller => {
      if (!shouldFetch) return;

      setLoading();

      setSimilarVehicles({
        searchRequestId: '',
        vehicles: []
      });

      fetchInventory(
        {
          ...request,
          analyticsData: generateAnalyticsData(),
          requestedFeatures: request.requestedFeatures.concat(
            ...conditionalRequestedFeaturesRef.current
          ),
          preferredAcquisitionName: preferredAcquisitionNameRef.current
        },
        controller
      )
        .then(res => {
          const {
            financeInfo,
            inventory,
            searchRequestId,
            tradeInfo,
            userDeliveryInfo
          } = res.data;
          const formattedFinanceInfo =
            financeInfo && !financeInfo.pricingHasErrored
              ? formatFinanceInfo(financeInfo)
              : undefined;
          setPreviousSearchRequestId(previousSearchRequestIdRef.current);
          setData({
            inventory,
            searchRequestId,
            userDeliveryInfo
          });
          if (request.requestedFeatures.includes('LoanTermPricing')) {
            setFinanceInfo(formattedFinanceInfo);
            setIsFinanceInitialized(true);
          }
          setInventoryResponse(res.data);
          setTradeInfo(tradeInfo ?? {});

          if (
            tradeInfo?.status === 'active' &&
            tradeInfo?.applyTradeIn != null
          ) {
            setTradeInApplied(tradeInfo.applyTradeIn);
          }
        })
        .catch(
          generateErrorLogger(
            'Inventory',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      generateAnalyticsData,
      request,
      setData,
      setError,
      setFinanceInfo,
      setIsFinanceInitialized,
      setLoading,
      setPreviousSearchRequestId,
      setSimilarVehicles,
      setTradeInApplied,
      setTradeInfo,
      shouldFetch
    ]
  );

  useEffectWithController(
    controller => {
      if (inventoryResponse && requestForLowInventoryRef.current) {
        void getLowInventory(
          requestForLowInventoryRef.current,
          inventoryResponse,
          controller
        );
      }
    },
    [getLowInventory, inventoryResponse]
  );
}
